import { useState } from 'react'
import { NavLink, Outlet, useLocation, Link } from 'react-router-dom'
import '@fontsource/material-icons/index.css'
import './dashboard.css'
import { navItems, isNavGroup, type NavGroup } from './navConfig'
import { useDashboardSession } from '../../hooks/useDashboardSession'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function userInitials(displayName: string | undefined, email: string): string {
  if (displayName) {
    return displayName
      .split(' ')
      .map((part) => part[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return (email[0] ?? '?').toUpperCase()
}

/** Flattens `navItems` into leaf `NavItem`s only, dropping the group wrappers. */
const flatNavItems = navItems.flatMap((entry) => (isNavGroup(entry) ? entry.children : [entry]))

function getPageTitle(pathname: string): string {
  // Exact match for index route first; then prefix match for sub-paths.
  const exact = flatNavItems.find((n) => n.path === pathname)
  if (exact) return exact.label

  const prefix = flatNavItems
    .filter((n) => n.path !== '/dashboard')
    .find((n) => pathname.startsWith(n.path))
  return prefix?.label ?? 'Dashboard'
}

/** True when `pathname` matches one of the group's children (used for auto-expand + active-group styling). */
function groupContainsPath(group: NavGroup, pathname: string): boolean {
  return group.children.some((child) => pathname === child.path || pathname.startsWith(`${child.path}/`))
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function DashboardLayout(): React.JSX.Element {
  const { user, logout } = useDashboardSession()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)

  // Group expand/collapse state, keyed by group label. A group with no entry
  // here falls back to its "auto" state (expanded iff the current route
  // matches one of its children) — this is fully derived from render rather
  // than synced via an effect, so navigating into a group's route expands it
  // automatically, and any explicit click (open or closed) sticks for the
  // rest of the session.
  const [groupOverrides, setGroupOverrides] = useState<Record<string, boolean>>({})

  const pageTitle = getPageTitle(location.pathname)
  const isAdmin = user.user_role === 'admin'

  const isGroupExpanded = (group: NavGroup): boolean =>
    groupOverrides[group.label] ?? groupContainsPath(group, location.pathname)

  const toggleGroup = (group: NavGroup): void => {
    setGroupOverrides((prev) => ({ ...prev, [group.label]: !isGroupExpanded(group) }))
  }

  // Filter admin-only items for non-admins — a group is dropped entirely if
  // adminOnly, and admin-only children are filtered out of visible groups.
  const visibleNavItems = navItems
    .filter((entry) => !entry.adminOnly || isAdmin)
    .map((entry) =>
      isNavGroup(entry)
        ? { ...entry, children: entry.children.filter((child) => !child.adminOnly || isAdmin) }
        : entry
    )
    .filter((entry) => !isNavGroup(entry) || entry.children.length > 0)

  const hasAvatar = Boolean(user.avatar_url)

  return (
    <div className={`glorify-dashboard${collapsed ? ' gd--collapsed' : ''}`}>
      {/* ── Sidebar ────────────────────────────────────────────────── */}
      <aside className="gd-sidebar" aria-label="Dashboard navigation">
        <div className="gd-sidebar__header">
          <Link to="/" className="gd-sidebar__brand" aria-label="SMETechFocus home">
            <div className="gd-sidebar__brand-mark">
              <span className="material-icons" aria-hidden="true">hub</span>
            </div>
            <div className="gd-sidebar__brand-text">
              <b>SMETechFocus</b>
              <span>Partners Portal</span>
            </div>
          </Link>

          <button
            className="gd-sidebar__toggle"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className="material-icons" aria-hidden="true">
              {collapsed ? 'chevron_right' : 'chevron_left'}
            </span>
          </button>
        </div>

        <nav className="gd-sidebar__nav">
          {visibleNavItems.map((item) =>
            isNavGroup(item) ? (
              <div className="gd-nav-group" key={item.label}>
                <button
                  type="button"
                  className={`gd-nav-group__toggle${
                    groupContainsPath(item, location.pathname) ? ' gd-nav-group__toggle--active' : ''
                  }`}
                  onClick={() => toggleGroup(item)}
                  aria-expanded={isGroupExpanded(item)}
                  aria-label={item.label}
                >
                  <span className="material-icons gd-nav-link__icon" aria-hidden="true">
                    {item.materialIcon}
                  </span>
                  <span className="gd-nav-link__label">{item.label}</span>
                  <span
                    className={`material-icons gd-nav-group__chevron${
                      isGroupExpanded(item) ? ' gd-nav-group__chevron--open' : ''
                    }`}
                    aria-hidden="true"
                  >
                    expand_more
                  </span>
                </button>

                {isGroupExpanded(item) && (
                  <div className="gd-nav-group__children">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        end={child.path === '/dashboard'}
                        className={({ isActive }) =>
                          `gd-nav-link gd-nav-subitem${isActive ? ' gd-nav-link--active' : ''}`
                        }
                        aria-label={child.label}
                      >
                        <span className="material-icons gd-nav-link__icon" aria-hidden="true">
                          {child.materialIcon}
                        </span>
                        <span className="gd-nav-link__label">{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/dashboard'}
                className={({ isActive }) =>
                  `gd-nav-link${isActive ? ' gd-nav-link--active' : ''}`
                }
                aria-label={item.label}
              >
                <span className="material-icons gd-nav-link__icon" aria-hidden="true">
                  {item.materialIcon}
                </span>
                <span className="gd-nav-link__label">{item.label}</span>
              </NavLink>
            )
          )}
        </nav>
      </aside>

      {/* ── Main area ──────────────────────────────────────────────── */}
      <div className="gd-main">
        {/* Top bar */}
        <header className="gd-topbar">
          <span className="gd-topbar__title">{pageTitle}</span>

          <div className="gd-topbar__user">
            <div className="gd-topbar__user-info" aria-hidden="true">
              {user.display_name && <b>{user.display_name}</b>}
              <small>{user.user_email}</small>
            </div>

            <div
              className="gd-avatar"
              role="img"
              aria-label={`Avatar for ${user.display_name ?? user.user_email}`}
            >
              {hasAvatar ? (
                <img src={user.avatar_url} alt="" />
              ) : (
                userInitials(user.display_name, user.user_email)
              )}
            </div>

            <button
              className="gd-logout-btn"
              onClick={() => void logout()}
              aria-label="Sign out"
            >
              <span className="material-icons" aria-hidden="true">logout</span>
              <span>Sign out</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="gd-content">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
